import { api } from '@udm/contracts';
import { fetchSites, request } from '../api/client';
import type { DeviceLive, DeviceStatic } from '../realtime/types';
import type { DataSource, Site } from './source';

/**
 * /api/v1 implementation of the DataSource port. Every endpoint is scoped server-side to the caller's sites
 * (a client account sees its whole client); the UI never filters for security.
 */
const qs = (p: Record<string, string | number | null | undefined>) => {
  const e = Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return e.length ? '?' + new URLSearchParams(e.map(([k, v]) => [k, String(v)])).toString() : '';
};

const toStatic = (d: api.RegistryDevice): DeviceStatic => ({
  id: d.id,
  name: d.name,
  deviceType: d.device_type,
  siteId: d.site_id,
  ...(d.project_id ? { projectId: d.project_id } : {}),
  ...(d.lon !== null && d.lat !== null ? { lon: d.lon, lat: d.lat } : {}),
  capabilities: d.capabilities,
  ...(d.protocol ? { protocol: d.protocol } : {}),
  ...(d.model ? { model: d.model } : {}),
  ...(d.link ? { link: d.link } : {}),
  ...(d.upstream ? { upstream: d.upstream } : {}),
});

const toLive = (l: api.DeviceLiveState): DeviceLive => ({
  sv: l.sv,
  presence: l.presence,
  presenceReason: l.presence_reason ?? null,
  presenceChangedAt: l.presence_changed_at ?? null,
  connectedSince: l.connected_since ?? null,
  alarm: l.alarm,
  metrics: l.metrics,
  reported: l.reported,
  ...(l.last_seen_at ? { lastSeenAt: l.last_seen_at } : {}),
  ...(l.measured_at ? { measuredAt: l.measured_at } : {}),
});

export const apiDataSource: DataSource = {
  kind: 'api',
  async summary(siteId) {
    const s = await request('GET', `/api/v1/summary${qs({ site_id: siteId })}`, undefined, (v) =>
      api.Summary.parse(v),
    );
    return {
      devices: s.devices,
      online: s.online,
      offline: s.offline,
      unknown: s.unknown,
      activeAlarms: s.active_alarms,
      gatewaysOnline: s.gateways_online,
      gatewaysTotal: s.gateways_total,
      byType: s.by_type,
    };
  },
  async listSites(): Promise<Site[]> {
    const { items } = await fetchSites();
    return items.map((s) => ({
      id: s.id,
      parentId: s.parent_id,
      kind: s.kind,
      name: s.name,
      deviceCount: s.device_count,
      ...(s.county ? { county: s.county } : {}),
      ...(s.lon !== null && s.lat !== null ? { lon: s.lon, lat: s.lat } : {}),
    }));
  },
  async listDevices({ siteId, search, cursor, limit }) {
    const r = await request(
      'GET',
      `/api/v1/devices${qs({ site_id: siteId, search, cursor, limit })}`,
      undefined,
      (v) => api.DeviceList.parse(v),
    );
    return { items: r.items.map(toStatic), nextCursor: r.next_cursor, total: r.total };
  },
  async getDevice(id) {
    try {
      const d = await request('GET', `/api/v1/devices/${id}`, undefined, (v) => api.DeviceDetail.parse(v));
      return {
        ...toStatic(d),
        lifecycle: d.lifecycle,
        ...(d.firmware ? { firmware: d.firmware } : {}),
        identifiers: d.identifiers,
        ...(d.gateway_key ? { gatewayKey: d.gateway_key } : {}),
        ...(d.connected_since ? { connectedSince: d.connected_since } : {}),
        ...(d.location_source ? { locationSource: d.location_source } : {}),
        canControl: d.can_control,
      };
    } catch {
      return null;
    }
  },
  async liveSnapshot(siteId) {
    const r = await request('GET', `/api/v1/devices/snapshot${qs({ site_id: siteId })}`, undefined, (v) =>
      api.DeviceSnapshot.parse(v),
    );
    return {
      asOfSeq: r.as_of_seq,
      devices: r.devices.map((d) => ({ static: toStatic(d.device), live: toLive(d.live) })),
    };
  },
  async listGateways() {
    const r = await request('GET', '/api/v1/gateways', undefined, (v) => api.GatewayList.parse(v)).catch(
      () => ({
        items: [],
      }),
    );
    return r.items.map((g) => ({
      id: g.id,
      key: g.key,
      name: g.name,
      kind: g.kind,
      status: g.status,
      softwareVersion: g.software_version ?? '—',
      connectedDevices: g.connected_devices,
      lastSeenAt: g.last_seen_at ?? '',
    }));
  },
  async listAlarms() {
    // Active alarms (the alarm centre pages through the full history itself).
    const r = await request('GET', '/api/v1/alarms?limit=500', undefined, (v) => api.AlarmPage.parse(v));
    return r.items.map((a) => ({
      id: a.id,
      deviceId: a.device_id,
      deviceName: a.device_name,
      siteId: a.site_id ?? '',
      severity: a.severity,
      state: a.state,
      ruleKey: a.alarm_type,
      openedAt: a.opened_at,
    }));
  },
};
